import React, { memo, useCallback, useContext, useMemo } from 'react'
import tw from 'twin.macro'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Tooltip } from '@mui/material'
import { useSelector } from 'react-redux'
import LogoBlack from 'src/assets/images/gigsafe-logo-black.svg'
import MarkBlack from 'src/assets/images/gigsafe-mark-black.svg'
import { Button } from 'src/components/shared/Button'
import { ADMIN_ROUTES } from 'src/constants/routes'
import { getCustomer } from 'src/selectors/customer'
import { getFlowEditorTemplate } from 'src/selectors/onboardingFlowEditor'
import { buildBreadcrumbs, resolveBreadcrumbBackPath } from 'src/utils/adminBreadcrumbs'
import { resolveCustomerDisplayName } from 'src/utils/customerList'
import { Breadcrumb } from './Breadcrumb'
import { UserMenuButton } from './UserMenuButton'
import { SideNavContext } from './SideNav.context'
import { NavigationGuardContext } from './NavigationGuard.context'
import { sideNavEntryStyles } from './sideNavEntryStyles'
import { topNavFadeInStyles } from './topNavFadeIn'
import { GSIcon } from 'src/components/shared/GSIcon'

/**
 * The 60px white bar across the whole viewport. Left to right: a logo block exactly as wide as
 * the side rail under it (so its right rule continues the rail's edge), then the route breadcrumb
 * trail 24px past that rule; the signed-in user's menu sits on the right. On the launcher the trail
 * would be a lone "Home" next to the logo, so it is omitted there. The rail's collapse toggle
 * lives at the foot of `SideNav`.
 */
const TopNavComponent = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams()
  const customer = useSelector(getCustomer)
  const flowEditorTemplate = useSelector(getFlowEditorTemplate)
  const { hasSideNav, isCollapsed } = useContext(SideNavContext)
  const { guardedNavigate } = useContext(NavigationGuardContext)

  // Only the customer this route is about: while a different one is still in state (mid switch)
  // the trail drops the segment rather than naming the wrong customer.
  const customerName =
    customer && customer.id === params.customerId ? resolveCustomerDisplayName(customer) : undefined
  // Same guard for the flow editor: the template crumb carries the loaded template's name only
  // when it is the one this route points at. Renames land in the same slice, so it updates live.
  const templateName =
    flowEditorTemplate && flowEditorTemplate.id === params.templateId
      ? flowEditorTemplate.name
      : undefined

  const crumbs = useMemo(() => {
    return buildBreadcrumbs(location.pathname, params, customerName, templateName)
  }, [location.pathname, params, customerName, templateName])

  const isLauncher = location.pathname === ADMIN_ROUTES.LAUNCHER_URL

  // "Back" goes to the previous screen, not through browser history — see
  // `resolveBreadcrumbBackPath` for what counts as a screen.
  const backPath = useMemo(() => {
    return resolveBreadcrumbBackPath(crumbs, location.pathname, params)
  }, [crumbs, location.pathname, params])

  // A page with unsaved work (the flow editor) registers a guarded navigate; Back honours it so
  // leaving mid-edit still asks first.
  const handleBack = useCallback(() => {
    if (guardedNavigate) {
      guardedNavigate(backPath)

      return
    }

    navigate(backPath)
  }, [guardedNavigate, navigate, backPath])
  const isRailCollapsed = hasSideNav && isCollapsed

  // Always the rail's width, rail or not: if the block sized itself to the logo on the launcher
  // and to 240px elsewhere, the trail would slide as the block grew on every entry into an app.
  const brandBlockWidth = isRailCollapsed ? tw`w-[60px]` : tw`w-[240px]`

  return (
    <header
      data-testid="admin-top-nav"
      css={tw`flex h-[60px] shrink-0 items-center justify-between gap-4 border-0 border-b border-solid border-shades-100 bg-shades-0 py-0 pr-6`}
    >
      <div css={tw`flex h-full min-w-0 flex-1 items-center`}>
        {/* Same width as the rail (240 expanded, 60 collapsed) and animated with it, so the trail
            starts where the page content does. Collapsed, 60px cannot hold the wordmark, so only
            the shield mark stays, centred like the rail's icons. */}
        <div
          css={[
            tw`flex h-full shrink-0 items-center overflow-hidden transition-[width] duration-200 ease-in-out motion-reduce:transition-none`,
            brandBlockWidth,
            isRailCollapsed ? tw`justify-center` : tw`px-4`,
            // The rule continues the rail's right edge up through the bar, so it only exists when
            // there is a rail to continue (not on the launcher).
            hasSideNav && tw`border-0 border-r border-solid border-shades-100`,
          ]}
        >
          <Link
            to={ADMIN_ROUTES.LAUNCHER_URL}
            aria-label="GigSafe Admin home"
            css={tw`flex shrink-0`}
          >
            {isRailCollapsed ? (
              <img
                key="mark"
                src={MarkBlack}
                alt="GigSafe"
                css={[tw`h-[25px]`, topNavFadeInStyles]}
                data-testid="admin-top-nav-mark"
              />
            ) : (
              <img
                key="logo"
                src={LogoBlack}
                alt="GigSafe"
                css={[tw`h-[25px]`, topNavFadeInStyles]}
                data-testid="admin-top-nav-logo"
              />
            )}
          </Link>
        </div>
        {!isLauncher ? (
          <div css={[tw`flex min-w-0 flex-1 items-center gap-4 pl-6`, topNavFadeInStyles]}>
            <Tooltip title="Back" placement="bottom">
              {/* Built exactly like a collapsed rail entry (36px square, same border, padding and
                  hover), so the two read as one family of controls. */}
              <Button
                size="small"
                color="white"
                className={[...sideNavEntryStyles(true, true), tw`mx-0 shrink-0`]}
                leftIcon={
                  <span css={tw`flex h-5 w-5 items-center justify-center text-shades-1000`}>
                    <GSIcon iconName="gs_chevron_left_small" size={20} />
                  </span>
                }
                aria-label="Back"
                onClick={handleBack}
                data-testid="admin-top-nav-back"
              />
            </Tooltip>
            <Breadcrumb crumbs={crumbs} />
          </div>
        ) : null}
      </div>
      <div css={tw`flex shrink-0 items-center gap-2`}>
        <UserMenuButton />
      </div>
    </header>
  )
}

export const TopNav = memo(TopNavComponent)
